import Image from "next/image";
import { AppRoutes } from "@/lib/routeResolver";
import { truncate } from "@/lib/helpers";
import type { GroupsPackage } from "@/lib/groups/packages";
import { formatDuration, PACKAGE_DESCRIPTION_MAX } from "./packages-format";
import PackagePrice from "./PackagePrice";
import PackageCities from "./PackageCities";
import styles from "./packages-gallery.module.css";

interface PackageGridCardProps {
  pack: GroupsPackage;
}

/** Card width changes a lot across the responsive grid (4 → 1 per row). */
const GRID_IMAGE_SIZES =
  "(max-width: 575px) 100vw, (max-width: 991px) 50vw, (max-width: 1199px) 33vw, 25vw";

/**
 * Vertical "grid" presentation of a groups package (4 per row on desktop).
 * Image on top with an overlaid duration badge, details below, and the actions
 * pinned to the bottom (`margin-top: auto`) so cards in a row stay aligned
 * regardless of text length. Same data/links as the list card.
 */
export default function PackageGridCard({ pack }: PackageGridCardProps) {
  const duration = formatDuration(pack.days, pack.nights);
  const summary = truncate(pack.description, PACKAGE_DESCRIPTION_MAX);
  const href = pack.pageURL
    ? AppRoutes.groupsPackage(pack.pageURL)
    : undefined;
  const flyerLabel = `${pack.flyerTitle} of ${pack.packageName}`;

  const img = pack.photoURL ? (
    <Image
      src={pack.photoURL}
      alt={pack.packageName}
      fill
      sizes={GRID_IMAGE_SIZES}
      style={{ objectFit: "cover" }}
    />
  ) : null;

  return (
    <article className={styles.gridCard}>
      <div className={styles.media}>
        {img &&
          (href ? (
            <a
              href={href}
              className={styles.mediaLink}
              aria-label={pack.packageName}
              title={pack.packageName}
            >
              {img}
            </a>
          ) : (
            img
          ))}
        {duration ? (
          <span className={styles.durationBadge}>{duration}</span>
        ) : null}
      </div>

      <div className={styles.body}>
        <h3 className={styles.title}>
          {href ? (
            <a href={href} title={pack.packageName}>
              {pack.packageName}
            </a>
          ) : (
            <span>{pack.packageName}</span>
          )}
        </h3>

        <PackageCities cities={pack.cities} />

        {summary ? <p className={styles.summary}>{summary}</p> : null}

        <div className={styles.footer}>
          <PackagePrice price={pack.startingPrice} />

          <div className={styles.actions}>
            {href ? (
              <a
                href={href}
                className={`th-btn2 style2 ${styles.viewBtn}`}
                title={`View Details of ${pack.packageName}`}
              >
                <span>View Details</span>
                <span className="visually-hidden">{` of ${pack.packageName}`}</span>
                <i className="fa fa-arrow-right ms-2" aria-hidden="true"></i>
              </a>
            ) : null}
            {pack.flyerUrl ? (
              <a
                href={pack.flyerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.flyerBtn}
                title={flyerLabel}
              >
                <i className="fa fa-download" aria-hidden="true"></i>
                <span className="visually-hidden">{flyerLabel}</span>
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}
